/**
 * `2q` — Perfis & permissões.
 *
 * 🔴 **DUAS colunas por linha, `Exibir` e `Editar`, e o shot desenha uma só.**
 * Isso é uma extensão deliberada do wireframe, fechada em 12/08 e **confirmada
 * pelo Eduardo em 13/08** — sem ela não há como expressar "o Cliente vê contratos
 * e não os altera", que é justamente uma decisão que ele tomou. As duas colunas
 * viram `nivel`: escrita = as duas marcadas, leitura = só `Exibir`, nenhum = as
 * duas vazias.
 *
 * 🔒 **BR-18 — o grupo NÃO carrega cliente.** Não existe campo de cliente nesta
 * tela e não pode existir: a permissão é funcional e global, e o vínculo grupo ×
 * cliente acontece no usuário (`2o`). O payload que sai daqui leva `permissoes` e
 * mais nada — se um dia levar um `idCliente`, a regra morreu no wire.
 *
 * 🔒 **As linhas vêm de `app/navigation.ts`**, e não de uma lista escrita à mão
 * aqui. É o mesmo lugar de onde a sidebar tira as abas, então o modelo de
 * navegação e o de permissão não têm como divergir: uma aba nova aparece nesta
 * árvore no mesmo commit em que aparece no menu.
 */
import { useState } from 'react'

import {
  ID_GRUPO_ADMINISTRADOR,
  type Grupo,
  type Permissao,
  type Usuario,
} from '@/api/contracts/administracao'
import {
  useCriarGrupo,
  useExcluirGrupo,
  useGrupos,
  useSalvarPermissoesDoGrupo,
  useUsuariosAdm,
} from '@/api/queries/administracao'
import { MODULOS } from '@/app/navigation'
import { useSession } from '@/auth/session'
import { EsqueletoDeLista } from '@/components/esqueleto'
import { EstadoDeErro } from '@/components/estados'
import { Botao, Entrada } from '@/components/primitivos'
import { usePermission } from '@/permissions/usePermission'

/** Uma linha da árvore: o módulo, ou uma aba indentada sob ele. */
type Linha = { chave: string; rotulo: string; aba: boolean; paiDe?: string[] }

/**
 * A árvore, derivada do modelo de navegação.
 *
 * `compliance.notas_ia` entra à mão porque NÃO é aba: é a coluna "Nota para IA"
 * do questionário (`3b`), uma permissão de campo (Compliance v4, 16/09/2026).
 * Deixá-la de fora tornaria impossível conceder ao Arquiteto o retorno sobre o
 * parecer da IA.
 */
function montarLinhas(): Linha[] {
  const linhas: Linha[] = []

  for (const modulo of MODULOS) {
    const filhos = modulo.abas.map((aba) => aba.permissao)
    linhas.push({ chave: modulo.permissao, rotulo: modulo.rotulo, aba: false, paiDe: filhos })
    for (const aba of modulo.abas) {
      linhas.push({ chave: aba.permissao, rotulo: aba.rotulo, aba: true })
    }
  }

  const indiceDoQuestionario = linhas.findIndex((l) => l.chave === 'compliance.questionario')
  linhas.splice(indiceDoQuestionario + 1, 0, {
    chave: 'compliance.notas_ia',
    rotulo: 'Nota para IA (coluna do questionário)',
    aba: true,
  })
  linhas.find((l) => l.chave === 'compliance')?.paiDe?.push('compliance.notas_ia')

  return linhas
}

const LINHAS = montarLinhas()

type Acesso = { ver: boolean; editar: boolean }

function paraMapa(permissoes: Permissao[]): Map<string, Acesso> {
  const mapa = new Map<string, Acesso>()
  for (const permissao of permissoes) {
    mapa.set(permissao.idFuncionalidade, {
      ver: permissao.nivel !== 'nenhum',
      editar: permissao.nivel === 'escrita',
    })
  }
  return mapa
}

function paraPermissoes(mapa: Map<string, Acesso>): Permissao[] {
  const permissoes: Permissao[] = []
  for (const [idFuncionalidade, acesso] of mapa) {
    // `nenhum` não é persistido: guardar a ausência de permissão encheria a
    // tabela de linhas que significam exatamente "não existe linha".
    if (acesso.editar) permissoes.push({ idFuncionalidade, nivel: 'escrita' })
    else if (acesso.ver) permissoes.push({ idFuncionalidade, nivel: 'leitura' })
  }
  return permissoes
}

export function Perfis() {
  const { editar } = usePermission('admin.perfis')
  const consulta = useGrupos()
  const criar = useCriarGrupo()

  const [aberto, setAberto] = useState<string | undefined>()
  const [criando, setCriando] = useState(false)
  const [nome, setNome] = useState('')
  const [excluido, setExcluido] = useState<string | null>(null)

  if (consulta.isPending) return <EsqueletoDeLista rotulo="Carregando grupos…" itens={4} />
  if (consulta.isError) {
    return <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="type-h4 text-text-primary">Perfis &amp; permissões (grupos)</h2>
        {editar && (
          <Botao variante="secundaria" icone="plus" onClick={() => setCriando((c) => !c)}>
            novo grupo
          </Botao>
        )}
      </div>

      {criando && (
        <form
          className="border-border-subtle bg-surface-card rounded-card flex flex-wrap items-center gap-3 border p-4"
          onSubmit={(evento) => {
            evento.preventDefault()
            criar.mutate(nome, {
              onSuccess: () => {
                setNome('')
                setCriando(false)
              },
            })
          }}
        >
          <Entrada
            required
            aria-label="Nome do grupo"
            placeholder="Gestor de contrato"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            className="max-w-xs"
          />
          <Botao variante="primaria" type="submit" disabled={criar.isPending}>
            {criar.isPending ? 'criando…' : 'criar grupo'}
          </Botao>
          <Botao variante="discreta" onClick={() => setCriando(false)}>
            cancelar
          </Botao>
          {criar.isError && (
            <div className="basis-full">
              <EstadoDeErro erro={criar.error} />
            </div>
          )}
        </form>
      )}

      {excluido && (
        <p role="status" className="text-success-700 type-sm">
          grupo {excluido} excluído
        </p>
      )}

      <div className="border-border-subtle bg-surface-card rounded-card overflow-hidden border">
        {consulta.data.map((grupo) => {
          const estaAberto = grupo.idGrupo === aberto
          return (
            <div key={grupo.idGrupo} className="border-border-subtle border-b last:border-b-0">
              <button
                type="button"
                aria-expanded={estaAberto}
                onClick={() => setAberto(estaAberto ? undefined : grupo.idGrupo)}
                className={`flex w-full items-center justify-between gap-3 px-5 py-4 text-left duration-fast ${
                  estaAberto ? 'bg-navy-50' : 'hover:bg-gray-50'
                }`}
              >
                <span>
                  <span className="text-text-primary type-body font-semibold">{grupo.nome}</span>{' '}
                  {/* ⚠️ Do CAMPO `padrao`, e não do formato do id. Até 28/08 esta
                      linha testava `idGrupo.startsWith('gr-')` — um prefixo que só
                      existia nas fixtures do frontend. O backend chama os mesmos
                      grupos de `administrador`/`arquiteto-iox`/`cliente`, então no
                      primeiro deploy TODO grupo apareceria como "criado pelo admin". */}
                  <span className="text-text-muted type-sm">
                    {grupo.padrao ? 'padrão' : 'criado pelo admin'}
                  </span>
                </span>
                <i
                  className={`ph text-icon-default ${estaAberto ? 'ph-caret-down' : 'ph-caret-right'}`}
                  aria-hidden="true"
                />
              </button>

              {estaAberto && (
                // 🔑 `key` no grupo: abrir outro grupo reinicia a árvore a partir
                // das permissões dele, sem efeito e sem risco de salvar as
                // marcações de um grupo no outro.
                <ArvoreDeVisibilidade
                  key={grupo.idGrupo}
                  idGrupo={grupo.idGrupo}
                  permissoes={grupo.permissoes}
                  editar={editar}
                />
              )}

              {/* Dentro do painel aberto, e não na linha: a linha inteira já é o
                  botão que expande, e um botão dentro de outro não é HTML válido. */}
              {estaAberto && editar && (
                <ExcluirGrupo
                  key={`excluir-${grupo.idGrupo}`}
                  grupo={grupo}
                  aoExcluir={() => {
                    setAberto(undefined)
                    setExcluido(grupo.nome)
                  }}
                />
              )}
            </div>
          )
        })}
      </div>

      <p className="rounded-card border-border-subtle bg-navy-50 text-text-body border p-4 type-sm">
        <i className="ph ph-info text-iox-navy mr-1.5" aria-hidden="true" />
        Grupos são <strong>globais</strong> (permissão funcional, sem vínculo a cliente) e criados
        dinamicamente. Permissão nunca é atribuída direto ao usuário; o vínculo grupo × cliente
        acontece no usuário, e os direitos de múltiplos grupos são <strong>somados</strong>.
      </p>
    </section>
  )
}

/**
 * Excluir um grupo — **em cascata**: ele sai de todo usuário que o tinha (decisão do
 * Fábio, 17/09/2026). Por isso a confirmação diz QUANTOS usuários perdem o grupo e
 * quantos ficam sem grupo nenhum, derivado de `GET /usuarios` como a `2n` deriva a
 * contagem de usuários por cliente. Ficar sem grupo é permitido: a pessoa entra e
 * não enxerga tela alguma.
 *
 * 🔒 O Administrador não oferece exclusão — o backend recusa com 409
 * `GRUPO_PROTEGIDO`, e um botão que sempre falha é pior que uma explicação.
 *
 * Confirmação inline com `role="alertdialog"`, o mesmo padrão da exclusão de
 * documento em Compliance.
 *
 * ⚠️ Se o usuário logado estava no grupo, as permissões DELE mudam: o `/eu` é
 * recarregado na hora (`recarregarPerfil`) em vez de esperar o próximo login.
 */
function ExcluirGrupo({ grupo, aoExcluir }: { grupo: Grupo; aoExcluir: () => void }) {
  const excluir = useExcluirGrupo()
  const usuarios = useUsuariosAdm()
  const { sessao, recarregarPerfil } = useSession()
  const [confirmando, setConfirmando] = useState(false)

  if (grupo.idGrupo === ID_GRUPO_ADMINISTRADOR) {
    return (
      <p className="text-text-muted px-5 pb-5 type-sm">
        <i className="ph ph-lock-simple text-iox-navy mr-1.5" aria-hidden="true" />O grupo
        Administrador não pode ser excluído: sem ele, ninguém conseguiria mais gerenciar perfis e
        permissões.
      </p>
    )
  }

  return (
    <div className="px-5 pb-5">
      {confirmando ? (
        <div
          role="alertdialog"
          aria-label={`Excluir o grupo ${grupo.nome}`}
          className="border-danger-500/30 bg-danger-100 rounded-card flex flex-col gap-3 border p-4"
        >
          <p className="text-text-body type-sm">
            Excluir o grupo <strong>{grupo.nome}</strong>?{' '}
            <ImpactoDaExclusao idGrupo={grupo.idGrupo} consulta={usuarios} />
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Botao
              variante="primaria"
              disabled={excluir.isPending || usuarios.isPending}
              onClick={() =>
                excluir.mutate(grupo.idGrupo, {
                  onSuccess: () => {
                    if (sessao?.grupos.includes(grupo.idGrupo)) void recarregarPerfil()
                    aoExcluir()
                  },
                })
              }
            >
              {excluir.isPending ? 'excluindo…' : 'excluir grupo'}
            </Botao>
            <Botao variante="discreta" onClick={() => setConfirmando(false)}>
              cancelar
            </Botao>
          </div>
          {excluir.isError && <EstadoDeErro erro={excluir.error} />}
        </div>
      ) : (
        <Botao
          variante="secundaria"
          icone="trash"
          className="text-danger-700"
          onClick={() => setConfirmando(true)}
        >
          excluir grupo
        </Botao>
      )}
    </div>
  )
}

function ImpactoDaExclusao({
  idGrupo,
  consulta,
}: {
  idGrupo: string
  consulta: { isPending: boolean; isError: boolean; data?: Usuario[] | undefined }
}) {
  if (consulta.isPending) return <>Contando os usuários afetados…</>
  if (consulta.isError || !consulta.data) {
    return (
      <>
        Não foi possível contar quantos usuários têm este grupo; todos que o tiverem vão perdê-lo.
      </>
    )
  }

  const afetados = consulta.data.filter((u) => u.grupos.includes(idGrupo))
  if (afetados.length === 0) return <>Nenhum usuário tem este grupo.</>

  const semGrupo = afetados.filter((u) => u.grupos.length === 1).length
  return (
    <>
      {afetados.length === 1
        ? '1 usuário perderá este grupo'
        : `${afetados.length} usuários perderão este grupo`}
      {semGrupo > 0 &&
        (semGrupo === 1
          ? ', e 1 deles ficará sem grupo nenhum (entra, mas não enxerga nenhuma tela)'
          : `, e ${semGrupo} deles ficarão sem grupo nenhum (entram, mas não enxergam nenhuma tela)`)}
      .
    </>
  )
}

function ArvoreDeVisibilidade({
  idGrupo,
  permissoes,
  editar,
}: {
  idGrupo: string
  permissoes: Permissao[]
  editar: boolean
}) {
  const salvar = useSalvarPermissoesDoGrupo()
  const [mapa, setMapa] = useState(() => paraMapa(permissoes))

  const acesso = (chave: string): Acesso => mapa.get(chave) ?? { ver: false, editar: false }

  function alternar(linha: Linha, coluna: 'ver' | 'editar', marcado: boolean) {
    setMapa((anterior) => {
      const proximo = new Map(anterior)
      const atual = proximo.get(linha.chave) ?? { ver: false, editar: false }

      // `Editar` implica `Exibir`. Poder alterar uma tela que não se enxerga é
      // um estado sem significado — e o resolver o trataria como escrita, o que
      // faria a árvore mentir sobre o que salvou.
      const novo =
        coluna === 'editar'
          ? { ver: marcado || atual.ver, editar: marcado }
          : { ver: marcado, editar: marcado ? atual.editar : false }

      proximo.set(linha.chave, novo)

      // Desmarcar o módulo desmarca as abas: uma aba visível dentro de um módulo
      // invisível não é alcançável por rota nenhuma, e deixá-la marcada faria a
      // tela prometer um acesso que não existe.
      if (!marcado && linha.paiDe) {
        for (const filho of linha.paiDe) proximo.set(filho, { ver: false, editar: false })
      }

      return proximo
    })
  }

  return (
    <div className="px-5 pb-5">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-border-subtle border-b">
            <th scope="col" className="text-text-muted py-2 text-left type-eyebrow">
              Tela / aba
            </th>
            <th scope="col" className="text-text-muted w-24 py-2 text-right type-eyebrow">
              Exibir
            </th>
            <th scope="col" className="text-text-muted w-24 py-2 text-right type-eyebrow">
              Editar
            </th>
          </tr>
        </thead>
        <tbody>
          {LINHAS.map((linha) => {
            const atual = acesso(linha.chave)
            return (
              <tr key={linha.chave} className="border-border-subtle border-b last:border-b-0">
                <td
                  className={`py-2.5 type-sm ${
                    linha.aba ? 'text-text-body pl-6' : 'text-text-primary font-semibold'
                  }`}
                >
                  {linha.rotulo}
                </td>
                {(['ver', 'editar'] as const).map((coluna) => (
                  <td key={coluna} className="py-2.5 text-right">
                    <input
                      type="checkbox"
                      // Rótulo específico: numa árvore de ~20 linhas, quarenta
                      // caixas chamadas "exibir" são indistinguíveis num leitor
                      // de tela.
                      aria-label={`${coluna === 'ver' ? 'Exibir' : 'Editar'} ${linha.rotulo}`}
                      checked={atual[coluna]}
                      disabled={!editar}
                      onChange={(e) => alternar(linha, coluna, e.target.checked)}
                      className="accent-iox-navy h-4 w-4"
                    />
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>

      <p className="text-text-muted mt-3 type-sm">
        <strong className="text-text-body">Exibir</strong> decide se a tela (ou a aba) aparece para
        o usuário; <strong className="text-text-body">Editar</strong> decide se ele pode alterar o
        que vê. Marcar <em>Editar</em> marca <em>Exibir</em> junto.
      </p>

      {editar && (
        <div className="mt-4 flex items-center gap-3">
          <Botao
            variante="primaria"
            disabled={salvar.isPending}
            onClick={() =>
              // 🔒 Só `permissoes`. Nenhum `idCliente` sai daqui (BR-18).
              salvar.mutate({ idGrupo, permissoes: paraPermissoes(mapa) })
            }
          >
            {salvar.isPending ? 'salvando…' : 'salvar permissões'}
          </Botao>
          {salvar.isSuccess && (
            <span role="status" className="text-success-700 type-sm">
              permissões salvas
            </span>
          )}
        </div>
      )}

      {salvar.isError && (
        <div className="mt-3">
          <EstadoDeErro erro={salvar.error} />
        </div>
      )}
    </div>
  )
}
